import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { ChevronDown, Trash2, Plus, Edit } from "lucide-react";
import "./CustomSelect.scss";

const CustomSelect = ({
  label,
  value,
  options,
  placeholder,
  onSelect,
  onDelete,
  onEdit,
  showDelete = false,
  showEdit = false,
  disabled = false,
  className = "",
  showCreateOption = false, // New prop to show/hide the create option
  createOptionText = "צור חדש", // Default text for create option
  onCreateOption = null, // Callback for when create option is clicked
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectClick = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (optionId) => {
    if (onSelect) {
      onSelect(optionId);
    }
    setIsOpen(false);
  };

  const handleDelete = (optionId, e) => {
    if (e) {
      e.stopPropagation();
    }
    if (onDelete) {
      onDelete(optionId);
    }
  };

  const handleEdit = (optionId, e) => {
    if (e) {
      e.stopPropagation();
    }
    if (onEdit) {
      onEdit(optionId);
    }
  };

  const handleCreateClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setIsOpen(false);
    if (onCreateOption) {
      onCreateOption();
    }
  };

  // Find the selected option for display
  const selectedOption = options.find(opt => opt.id === value);

  return (
    <div className={`training-select-wrapper ${className}`} ref={dropdownRef}>
      {label && (
        <div className="training-select-header">
          <label className="training-select-label">{label}</label>
        </div>
      )}
      <div className="training-select-container">
        <div
          className={`training-select-button ${isOpen ? "open" : ""} ${value ? "selected" : ""} ${disabled ? "disabled" : ""}`}
          onClick={handleSelectClick}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className="training-select-button-text">
            {selectedOption ? (
              <div className="training-select-value-container">
                {selectedOption.icon && <selectedOption.icon className="training-select-value-icon" size={18} />}
                <span>{selectedOption.label}</span>
              </div>
            ) : (
              placeholder
            )}
          </span>
          <ChevronDown
            className={`training-select-chevron ${isOpen ? "open" : ""}`}
            size={20}
          />
        </div>
        {isOpen && (
          <div className="training-select-custom-dropdown">
            <ul className="training-select-options-list" role="listbox">
              {/* Create New Option */}
              {showCreateOption && onCreateOption && (
                <li
                  className="training-select-option-item training-create-option"
                  onClick={handleCreateClick}
                  role="option"
                >
                  <div className="training-select-option-content training-create-option-content">
                    <Plus className="training-select-option-icon" size={18} />
                    <span>{createOptionText}</span>
                  </div>
                </li>
              )}
              
              {/* Divider if both create option and regular options exist */}
              {showCreateOption && onCreateOption && options.length > 0 && (
                <li className="training-select-divider"></li>
              )}
              
              {/* Regular Options */}
              {options.length > 0 ? (
                options.map((option) => {
                  const IconComponent = option.icon;
                  const uniqueKey = String(option.id || `option-${option.label}`);
                  
                  return (
                    <li
                      key={uniqueKey}
                      className={`training-select-option-item ${
                        value === option.id ? "selected" : ""
                      }`}
                      onClick={() => handleOptionClick(option.id)}
                      role="option"
                      aria-selected={value === option.id}
                    >
                      <div className="training-select-option-content">
                        {IconComponent && <IconComponent className="training-select-option-icon" size={18} />}
                        <span>{option.label}</span>
                      </div>
                      <div className="training-select-option-actions">
                        {showEdit && (
                          <button
                            onClick={(e) => handleEdit(option.id, e)}
                            className="training-button training-button-secondary training-button-small"
                            aria-label={`Edit ${option.label}`}
                          >
                            <Edit size={14} />
                          </button>
                        )}
                        {showDelete && (
                          <button
                            onClick={(e) => handleDelete(option.id, e)}
                            className="training-button training-button-danger training-button-small"
                            aria-label={`Delete ${option.label}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })
              ) : (
                !showCreateOption && (
                  <li className="training-select-empty-state">
                    אין אפשרויות זמינות
                  </li>
                )
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

CustomSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType
    })
  ).isRequired,
  placeholder: PropTypes.string,
  onSelect: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  showDelete: PropTypes.bool,
  showEdit: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  showCreateOption: PropTypes.bool,
  createOptionText: PropTypes.string,
  onCreateOption: PropTypes.func
};

export default CustomSelect;